import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { TrendingUp, Users, FileText, Settings, LogOut, Plus, Mail, Download, BarChart3, CheckCircle, AlertTriangle, UserPlus } from 'lucide-react';
import { useAuth } from '../App';
import BrandLink from './BrandLink';
import InviteClient from './InviteClient';
import ClientsList from './ClientsList';
import DeepAnalysisTester from './DeepAnalysisTester';
import axios from 'axios';

import { BACKEND_URL } from "../utils/apiBase";
const API = `${BACKEND_URL}/api`;

const ProfessionalPortal = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <BrandLink />
              <span className="text-sm text-gray-500">Professional Portal</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {user?.full_name}</span>
              <button
                onClick={logout}
                className="flex items-center space-x-1 text-gray-600 hover:text-gray-900"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 bg-white rounded-lg shadow-sm p-6 mr-8">
            <nav className="space-y-2">
              <NavLink to="/professional" icon={BarChart3} active={location.pathname === '/professional'}>
                Dashboard
              </NavLink>
              <NavLink to="/professional/clients" icon={Users} active={location.pathname === '/professional/clients'}>
                Clients
              </NavLink>
              <NavLink to="/professional/invite-client" icon={UserPlus} active={location.pathname === '/professional/invite-client'}>
                Invite Client
              </NavLink>
              <NavLink to="/professional/deep-analysis-tester" icon={FileText} active={location.pathname === '/professional/deep-analysis-tester'}>
                Deep Analysis (MVP)
              </NavLink>
              <NavLink to="/professional/profile" icon={Settings} active={location.pathname === '/professional/profile'}>
                Profile
              </NavLink>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="clients" element={<ClientsList />} />
              <Route path="invite-client" element={<InviteClient />} />
              <Route path="deep-analysis-tester" element={<DeepAnalysisTester />} />
              <Route path="profile" element={<ProfessionalProfile />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

const NavLink = ({ to, icon: Icon, children, active }) => (
  <Link
    to={to}
    className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors duration-200 ${
      active
        ? 'bg-blue-100 text-blue-700'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
    }`}
  >
    <Icon className="w-5 h-5" />
    <span>{children}</span>
  </Link>
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalClients: 0,
    activeAnalyses: 0,
    thisMonthAnalyses: 0,
    avgHealthScore: 0
  });
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [clientsResponse, analysesResponse] = await Promise.all([
        axios.get(`${API}/professionals/clients`),
        // We'll need to create this endpoint
        axios.get(`${API}/professionals/dashboard-stats`)
      ]);

      const clients = clientsResponse.data;
      setStats({
        totalClients: clients.length,
        activeAnalyses: clients.reduce((sum, client) => sum + client.analyses_count, 0),
        thisMonthAnalyses: 0, // Calculate from recent analyses
        avgHealthScore: 0 // Calculate average
      });

      // Get recent analyses from all clients
      const allAnalyses = [];
      for (const client of clients.slice(0, 5)) { // Limit for performance
        if (client.latest_analysis) {
          allAnalyses.push({
            ...client.latest_analysis,
            client_name: client.full_name
          });
        }
      }
      setRecentAnalyses(allAnalyses.slice(0, 10));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Overview of your client portfolio and recent activity</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Total Clients"
          value={stats.totalClients}
          icon={Users}
          color="blue"
        />
        <StatsCard
          title="Total Analyses"
          value={stats.activeAnalyses}
          icon={FileText}
          color="green"
        />
        <StatsCard
          title="This Month"
          value={stats.thisMonthAnalyses}
          icon={BarChart3}
          color="purple"
        />
        <StatsCard
          title="Avg Health Score"
          value={`${stats.avgHealthScore}/100`}
          icon={TrendingUp}
          color="orange"
        />
      </div>

      {/* Recent Analyses */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Client Analyses</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {recentAnalyses.length > 0 ? (
            recentAnalyses.map((analysis, index) => (
              <div key={index} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{analysis.client_name}</h3>
                    <p className="text-sm text-gray-600">{analysis.filename}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(analysis.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      analysis.health_score >= 80 
                        ? 'bg-green-100 text-green-800'
                        : analysis.health_score >= 60
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {analysis.health_score}/100
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-gray-500">
              No recent analyses available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatsCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};





const ProfessionalProfile = () => {
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/professionals/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Profile doesn't exist yet, show form to create one
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Professional Profile</h1>
        <p className="text-gray-600">Manage your professional information and credentials</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <p className="text-gray-600">Professional profile management coming soon...</p>
      </div>
    </div>
  );
};

const ClientDetail = () => {
  // Component for viewing individual client details and analyses
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Client Details</h1>
      <p className="text-gray-600">Coming soon...</p>
    </div>
  );
};

export default ProfessionalPortal;
